import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import type { Product } from '@/data/products';
import { prefetchProduct, seedProductCache } from '@/data/products.queries';
import { r2Url } from '@/lib/r2';
import { useRelatedProducts } from '../useRelatedProducts';

const PLACEHOLDER_IMG = 'https://placehold.co/800x1000/png?text=No+Image';

export default function RelatedProducts({ currentSlug }: { currentSlug?: string }) {
  const queryClient = useQueryClient();
  const { relatedProducts, isLoading } = useRelatedProducts(currentSlug);

  const handlePrefetch = (product: Product) => {
    if (!product.slug) return;
    seedProductCache(queryClient, product);
    prefetchProduct(queryClient, product.slug);
  };

  if (isLoading && relatedProducts.length === 0) {
    return (
      <div className="mt-14 rounded-[18px] border border-[#e6e6e6] bg-white p-6 text-sm font-semibold text-[#747982] shadow-sm">
        Loading related products...
      </div>
    );
  }

  return (
    <section className="mt-16">
      <div className="mb-6 flex items-end justify-between gap-4">
        <div>
          <p className="text-xs font-black uppercase tracking-[0.18em] text-[#777d86]">KG Goods</p>
          <h2 className="mt-2 text-3xl font-black text-[#111318]">
            Танд таалагдаж магадгүй
          </h2>
        </div>
        <Link to="/products" className="hidden items-center gap-2 text-sm font-black text-[#111318] transition hover:text-[#b87900] sm:flex">
          Бүгдийг харах <ArrowRight size={17} />
        </Link>
      </div>

      <div className="grid grid-cols-2 gap-4 sm:gap-5 lg:grid-cols-4">
        {relatedProducts.map((product) => {
          const imgSrc =
            r2Url(product.image_path ?? product.gallery_paths?.[0] ?? '') || PLACEHOLDER_IMG;

          return (
            <Link
              key={product.id}
              to={`/product/${product.slug}`}
              state={{ product }}
              className="group block overflow-hidden rounded-[18px] border border-[#e6e6e6] bg-white shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
              onMouseEnter={() => handlePrefetch(product)}
              onFocus={() => handlePrefetch(product)}
              onTouchStart={() => handlePrefetch(product)}
            >
              <div className="relative aspect-[4/5] overflow-hidden bg-[#f7f7f7]">
                <img
                  src={imgSrc}
                  alt={product.name}
                  className="h-full w-full object-contain p-4 transition-transform duration-500 group-hover:scale-105"
                  loading="lazy"
                  onError={(event) => {
                    const img = event.currentTarget;
                    if (img.src !== PLACEHOLDER_IMG) img.src = PLACEHOLDER_IMG;
                  }}
                />

                {product.originalPrice && (
                  <span className="absolute right-2 top-2 rounded-full bg-[#ef4444] px-2 py-1 text-[10px] font-black text-white">
                    ХЯМДРАЛ
                  </span>
                )}
                {product.is_new && (
                  <span className="absolute left-2 top-2 rounded-full bg-[#18a970] px-2 py-1 text-[10px] font-black text-white">
                    ШИНЭ
                  </span>
                )}
              </div>

              <div className="p-4">
                <h3 className="line-clamp-2 text-sm font-black text-[#25282d] transition-colors group-hover:text-[#111318] sm:text-base">
                  {product.name}
                </h3>
                <div className="mt-1 flex items-center gap-1">
                  <div className="flex text-xs text-yellow-400">★★★★☆</div>
                </div>
                <div className="mt-2 flex items-center gap-2">
                  <span className="font-black text-[#f6a800]">₮{Number(product.price).toLocaleString()}</span>
                  {product.originalPrice && (
                    <span className="text-xs font-semibold text-[#9aa0a8] line-through">
                      ₮{Number(product.originalPrice).toLocaleString()}
                    </span>
                  )}
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </section>
  );
}
